import {
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
} from '@ag-grid-community/core';
import { isArray } from 'lodash';

import { getFileNameFromFileKey } from 'src/legacy/components/Files/helpers';
import {
  FormResponse,
  FormResponseStatus,
  FormTemplate,
  InputsTypes,
} from 'src/legacy/components/FormsV2/formsTypes';
import { Client } from 'src/store/clients/types';
import { DateUtils } from 'src/utils';
import CSVUtils from 'src/utils/CSVUtils';

export const SUBMISSION_TIME_FORMAT = 'MM/DD/YYYY hh:mm:ss a';

/**
 * This function returns the file names of the files uploaded for a file upload question answer.
 * @param filesKeys: the files keys to get the file names from
 * @returns file names separated by comma
 */
export const getFileUploadQuestionAnswerFileNames = (
  filesKeys: string | string[],
) => {
  let files = filesKeys;
  if (typeof files == 'string') {
    files = [files];
  }

  return files
    .map((key: string) =>
      getFileNameFromFileKey(key, {
        withExtension: true,
      }),
    )
    .join(', ');
};

/**
 * This function handles exporting the forms response to csv.
 * @param formsResponses the forms responses to export
 * @param fileNameParts the file name parts to be used in the exported file name
 * @param clients client redux state used to lookup the client who submitted the response
 */
export const exportFormResponses = (
  formsResponses: FormResponse[] | undefined,
  fileNameParts: string,
  clients: Client[],
  selectedForm: FormTemplate,
) => {
  /**
   * IMPORTANT: This export logic parallels the CSV column generation in FormDetailsPageToolbar.tsx
   * Any changes here should be reflected in the csvColumnKeys calculation to maintain
   * consistent export behavior across the application.
   *
   * The field order follows:
   * 1. Basic info (responder, email, submission date)
   * 2. Current form template fields
   * 3. Fields from previous versions (found in responses but not in current template)
   */

  // Get current template fields (matches logic in FormDetailsPageToolbar.csvColumnKeys)
  const currentFieldIds =
    selectedForm.fields.formFields
      .filter((x) => x.type !== InputsTypes.Title)
      .map((x) => x.formFieldId)
      .filter(Boolean) || [];
  // the responses headers stores the questions titles
  // initially add the responder name header which represents the form response owner
  const csvHeaders = ['Responder', 'Email', 'Submitted on'];
  const csvRows: string[][] = [];

  // Collect field metadata from the template
  const templateFieldMap = Object.fromEntries(
    selectedForm.fields.formFields
      .filter((f) => f.formFieldId && f.type !== InputsTypes.Title)
      .map((f) => [f.formFieldId!, f.title]),
  );

  // Collect all unique field IDs from responses
  const responseFieldMap: Record<string, string> = {};
  for (const response of formsResponses || []) {
    for (const [id, field] of Object.entries(
      response.fields.formFields || {},
    )) {
      if (!responseFieldMap[id] && field?.type !== InputsTypes.Title) {
        responseFieldMap[id] = field.title;
      }
    }
  }

  // Determine deleted fields
  const deletedFieldIds = Object.keys(responseFieldMap).filter(
    (id) => !currentFieldIds.includes(id),
  );

  const finalFieldOrder = [...currentFieldIds, ...deletedFieldIds];

  const fieldIdToTitleMap = { ...responseFieldMap, ...templateFieldMap };

  for (const fieldId of finalFieldOrder) {
    csvHeaders.push(fieldIdToTitleMap[fieldId] || 'Untitled');
  }

  // Process each response and build CSV rows
  for (const response of formsResponses || []) {
    const client = clients.find((c) => c.id === response.clientId);
    if (!client) continue;

    const { givenName, familyName, email } = client.fields;
    const responderFullName = `${givenName || ''} ${familyName || ''}`.trim();
    const isCompleted = response.fields.status === FormResponseStatus.Completed;
    const submissionDate = isCompleted
      ? DateUtils.formatDate(
          response.fields.submissionDate,
          'MM/DD/YYYY hh:mm:ss a',
        )
      : '';

    const row: string[] = [responderFullName, email, submissionDate];
    for (const fieldId of finalFieldOrder) {
      const field = response.fields.formFields?.[fieldId];

      if (!field || field.type === InputsTypes.Title) {
        row.push('');
        continue;
      }

      switch (field.type) {
        case InputsTypes.MultiSelect:
        case InputsTypes.SingleSelect:
          if (Array.isArray(field.answer)) {
            row.push(field.answer.join(', '));
          } else {
            row.push(field.answer || '');
          }
          break;
        case InputsTypes.FileUpload:
          row.push(getFileUploadQuestionAnswerFileNames(field.answer || []));
          break;
        case InputsTypes.Date:
          row.push(DateUtils.formatDate(field.answer));
          break;
        default:
          if (Array.isArray(field.answer)) {
            row.push(field.answer.join(', '));
          } else {
            row.push(field.answer || '');
          }
      }
    }

    csvRows.push(row);
  }

  // Export the final CSV file
  const fileDate = DateUtils.formatDate(Date.now());
  const csvFileName = `form_${fileNameParts}_${fileDate}.csv`;
  CSVUtils.exportDataToCsv(csvFileName, [csvHeaders, ...csvRows]);
};

// SECTION: Form Templates Table Export Handlers
export const processFormTemplatesTableHeader = (
  params: ProcessHeaderForExportParams,
) => {
  let headerVal: string;
  switch (params.column.getColId()) {
    case 'notificationCount':
      headerVal = '';
      break;
    case 'creator':
      // for client column we want to create three using hardcoded " and , to display full info
      headerVal = 'Creator first name","Creator last name","Creator email';
      break;
    case 'responseCount':
      headerVal = 'Responses';
      break;
    default:
      headerVal = params.column.getColDef().headerName || '';
  }
  return `"${headerVal}"`;
};

/**
 * Format the row data for CSV export - some row data may be formatted in a specific way
 * @param params object containing column ID, and node data containing other information for the row
 * */
export const processFormTemplateCell = (params: ProcessCellForExportParams) => {
  let cellVal = '';
  const formRowData = params.node?.data;
  switch (params.column.getColId()) {
    case 'notificationCount':
      cellVal = '';
      break;
    case 'creator': {
      cellVal = `${formRowData.creator.metadata.givenName || ''}","${
        formRowData.creator.metadata.familyName || ''
      }","${formRowData.creator.metadata.email || ''}`;
      break;
    }
    case 'responseCount': {
      cellVal = `${formRowData.responseCount.responded}`;
      break;
    }

    case 'waitForResponse': {
      cellVal = `${formRowData.waitForResponse}`;
      break;
    }
    case 'createdAt': {
      cellVal = `${DateUtils.formatDate(formRowData.createdAt)}`;
      break;
    }

    case 'latestResponse': {
      cellVal = formRowData.latestResponse
        ? `${DateUtils.formatDate(formRowData.latestResponse)}`
        : '';
      break;
    }

    default:
      cellVal = params.value || '';
  }

  return `"${cellVal}"`;
};

// SECTION: Form Response Table Export Handlers
/**
 * Format the column for CSV export - if not an exception just return the column header name
 * @param params object containing columnID and other information required to know which header issued this callback
 * */
export const processFormResponseHeader = (
  params: ProcessHeaderForExportParams,
) => {
  const columnDef = params.column.getColDef();
  if (columnDef.field === 'responder') return 'Responder, Email';
  // remove commas from the header title
  const sanitizedHeaderTitle = columnDef.headerComponentParams.title;
  return `"${sanitizedHeaderTitle}"`;
};

/**
 * Format the row data for CSV export - some row data may be formatted in a specific way
 * @param params object containing column ID, and node data containing other information for the row
 * */
export const processFormResponseCell = (params: ProcessCellForExportParams) => {
  const nodeData = params.node?.data;
  const columnId = params.column.getColId();
  const questionType = params.column.getColDef().headerComponentParams?.type;

  if (columnId === 'responder')
    return `${nodeData.responder.givenName} ${nodeData.responder.familyName}, ${nodeData.responder.email}`;

  if (columnId === 'submissionDate') {
    const submissionTime = new Date(nodeData[columnId]);
    return `${submissionTime.toLocaleDateString()} at ${submissionTime.toLocaleTimeString()}`;
  }

  let cellVal = undefined;

  if (typeof nodeData[columnId] === 'string') {
    cellVal = nodeData[columnId].replaceAll(/"/g, '""');
  } else if (
    isArray(nodeData[columnId]) &&
    (questionType === InputsTypes.MultiSelect ||
      questionType === InputsTypes.SingleSelect)
  ) {
    cellVal = nodeData[columnId].join(', ');
  }

  return `"${cellVal || ''}"`;
};
